import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, combineLatest, map, Observable, of, shareReplay, Subscription } from 'rxjs'

import { CommentAnchorSummary } from '../extensions/comment-anchor-utils'
import { anchorKey, EditorThread, SidebarFilter } from '../editor-thread.types'
import { toCommentHeaderMeta } from '../editor-thread.selectors'
import { buildFormattingChangeLabels, collectListChangeLabelDescriptors, SuggestionSummary, SuggestionTextRun } from '../extensions/track-changes-utils'
import { ColorChange } from '../../../../../../backend/src/tiptap/editor/extensions/marks/suggestion'
import { formatRelativeTime } from '../utils/format-relative-time'
import { ThreadCommentsCurrentUser } from './thread-comments/thread-comments.component'
import { SuggestionCardComponent, SuggestionCardVariant } from './suggestion-card/suggestion-card.component'
import { CommentCardComponent } from './comment-card/comment-card.component'

const HIGHLIGHT_DURATION_MS = 1500

type SuggestionCardData = {
  variant: SuggestionCardVariant
  authorName: string
  createdAtRelative: string | null
  insertionRuns: SuggestionTextRun[]
  deletionRuns: SuggestionTextRun[]
  formattingText: string
  structureText: string
  thread: EditorThread | null
}

type CommentCardData = {
  quotedText: string
  thread: EditorThread | null
  authorName: string | null
  createdAtRelative: string | null
  isDraft: boolean
  orphaned: boolean
}

type SidebarCard =
  ({ kind: 'suggestion'; id: string; position: number } & SuggestionCardData) | ({ kind: 'comment'; id: string; position: number } & CommentCardData)

@Component({
  selector: 'app-tiptap-editor-sidebar',
  standalone: true,
  imports: [CommonModule, TranslatePipe, SuggestionCardComponent, CommentCardComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tiptap-editor-sidebar.component.html',
  styleUrl: './tiptap-editor-sidebar.component.scss',
})
export class TiptapEditorSidebarComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input({ required: true })
  suggestions$!: Observable<SuggestionSummary[]>

  @Input({ required: true })
  activeSuggestionId$!: Observable<string | null>

  @Input({ required: true })
  threads$!: Observable<Map<string, EditorThread>>

  @Input({ required: true })
  commentAnchors$!: Observable<CommentAnchorSummary[]>

  @Input({ required: true })
  activeCommentAnchorId$!: Observable<string | null>

  @Input({ required: true })
  activeCommentDraftId$!: Observable<string | null>

  @Input({ required: true })
  canResolve!: boolean

  @Input({ required: true })
  canResolveComment!: boolean

  @Input({ required: true })
  currentUser!: ThreadCommentsCurrentUser

  @Input()
  canPostComment = true

  @Input()
  filter: SidebarFilter = 'all'

  @Output()
  filterChange = new EventEmitter<SidebarFilter>()

  @Output()
  accept = new EventEmitter<string>()

  @Output()
  reject = new EventEmitter<string>()

  @Output()
  view = new EventEmitter<string>()

  @Output()
  submitComment = new EventEmitter<{ suggestionId: string; body: string }>()

  @Output()
  viewComment = new EventEmitter<string>()

  @Output()
  resolveComment = new EventEmitter<string>()

  @Output()
  discardComment = new EventEmitter<string>()

  @Output()
  submitCommentText = new EventEmitter<{ anchorId: string; body: string }>()

  @ViewChildren('cardRef', { read: ElementRef })
  cardRefs: QueryList<ElementRef<HTMLElement>> = new QueryList()

  highlightedCardId: string | null = null
  allCards$: Observable<SidebarCard[]> = of([])
  visibleCards$: Observable<SidebarCard[]> = of([])
  changesCount$: Observable<number> = of(0)
  commentsCount$: Observable<number> = of(0)

  private filterSubject = new BehaviorSubject<SidebarFilter>('all')
  private translateService = inject(TranslateService)
  private changeDetectorRef = inject(ChangeDetectorRef)
  private destroyRef = inject(DestroyRef)
  private activeSuggestionSubscription: Subscription | null = null
  private activeCommentAnchorSubscription: Subscription | null = null
  private highlightTimeoutId: number | null = null

  ngOnChanges(changes: SimpleChanges) {
    if ('filter' in changes) {
      this.filterSubject.next(this.filter)
    }
    if ('suggestions$' in changes || 'threads$' in changes || 'commentAnchors$' in changes || 'activeCommentDraftId$' in changes) {
      this.allCards$ = combineLatest([this.suggestions$, this.threads$, this.commentAnchors$, this.activeCommentDraftId$]).pipe(
        map(([summaries, threads, commentAnchors, draftId]) => this.buildCards(summaries, threads, commentAnchors, draftId)),
        shareReplay({ bufferSize: 1, refCount: true })
      )
      this.visibleCards$ = combineLatest([this.allCards$, this.filterSubject]).pipe(map(([cards, filter]) => this.applyFilter(cards, filter)))
      this.changesCount$ = this.allCards$.pipe(map((cards) => cards.filter((card) => card.kind === 'suggestion').length))
      this.commentsCount$ = this.allCards$.pipe(map((cards) => cards.filter((card) => card.kind === 'comment').length))
    }
    if ('activeSuggestionId$' in changes) {
      this.activeSuggestionSubscription?.unsubscribe()
      this.activeSuggestionSubscription = this.subscribeActiveCard(this.activeSuggestionId$)
    }
    if ('activeCommentAnchorId$' in changes) {
      this.activeCommentAnchorSubscription?.unsubscribe()
      this.activeCommentAnchorSubscription = this.subscribeActiveCard(this.activeCommentAnchorId$)
    }
  }

  private subscribeActiveCard(activeId$: Observable<string | null>): Subscription {
    return activeId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      if (id !== null) {
        this.highlightCard(id)
      }
    })
  }

  ngAfterViewInit() {
    if (this.highlightedCardId !== null) {
      this.scrollHighlightedCardIntoView()
    }
  }

  ngOnDestroy() {
    if (this.highlightTimeoutId !== null) {
      clearTimeout(this.highlightTimeoutId)
    }
  }

  selectTab(tab: SidebarFilter) {
    this.filterChange.emit(tab)
  }

  onAccept(suggestionId: string) {
    this.accept.emit(suggestionId)
  }

  onReject(suggestionId: string) {
    this.reject.emit(suggestionId)
  }

  onView(suggestionId: string) {
    this.view.emit(suggestionId)
  }

  onSubmitComment(suggestionId: string, body: string) {
    this.submitComment.emit({ suggestionId, body })
  }

  onViewComment(anchorId: string) {
    this.viewComment.emit(anchorId)
  }

  onResolveComment(anchorId: string) {
    this.resolveComment.emit(anchorId)
  }

  onDiscardComment(anchorId: string) {
    this.discardComment.emit(anchorId)
  }

  onSubmitCommentText(anchorId: string, body: string) {
    this.submitCommentText.emit({ anchorId, body })
  }

  private applyFilter(cards: SidebarCard[], filter: SidebarFilter): SidebarCard[] {
    if (filter === 'changes') {
      return cards.filter((card) => card.kind === 'suggestion')
    }
    if (filter === 'comments') {
      return cards.filter((card) => card.kind === 'comment')
    }
    return cards
  }

  private buildCards(
    summaries: SuggestionSummary[],
    threads: Map<string, EditorThread>,
    commentAnchors: CommentAnchorSummary[],
    draftId: string | null
  ): SidebarCard[] {
    const fallbackAuthor = this.translateService.instant('EDITOR.TRACK_CHANGES.UNKNOWN_AUTHOR')
    const suggestionCards: SidebarCard[] = summaries.map((summary) => this.toSuggestionCard(summary, fallbackAuthor, threads))
    const commentCards: SidebarCard[] = this.toCommentCards(commentAnchors, threads, draftId)
    return [...suggestionCards, ...commentCards].sort((left, right) => left.position - right.position)
  }

  private toSuggestionCard(summary: SuggestionSummary, fallbackAuthor: string, threads: Map<string, EditorThread>): SidebarCard {
    const formattingText =
      summary.category === 'formatting' ? this.formatFormattingLabels(summary.formattingAdded, summary.formattingRemoved, summary.colorChange) : ''
    const structureText = this.formatListChangeLabels(summary.listChanges)

    return {
      kind: 'suggestion',
      id: summary.id,
      position: summary.position,
      variant: summary.category,
      authorName: summary.authorName ?? fallbackAuthor,
      createdAtRelative: formatRelativeTime(summary.createdAt),
      insertionRuns: summary.insertionRuns,
      deletionRuns: summary.deletionRuns,
      formattingText,
      structureText,
      thread: threads.get(anchorKey('SUGGESTION', summary.id)) ?? null,
    }
  }

  private toCommentCards(commentAnchors: CommentAnchorSummary[], threads: Map<string, EditorThread>, draftId: string | null): SidebarCard[] {
    const anchoredIds = new Set(commentAnchors.map((anchor) => anchor.anchorId))
    const anchoredCards: SidebarCard[] = commentAnchors.map((anchor) => {
      const thread = threads.get(anchorKey('TEXT_RANGE', anchor.anchorId)) ?? null
      return {
        kind: 'comment',
        id: anchor.anchorId,
        position: anchor.position,
        quotedText: anchor.quotedText,
        thread,
        ...toCommentHeaderMeta(thread),
        isDraft: anchor.anchorId === draftId,
        orphaned: false,
      }
    })

    // A thread whose anchor mark was deleted from the document still lives in the
    // sidebar as an orphan, appended at the end since it has no document position.
    const orphanCards: SidebarCard[] = Array.from(threads.values())
      .filter((thread) => thread.anchorType === 'TEXT_RANGE' && !anchoredIds.has(thread.anchorId) && thread.comments.length > 0)
      .map((thread) => ({
        kind: 'comment',
        id: thread.anchorId,
        position: Number.MAX_SAFE_INTEGER,
        quotedText: '',
        thread,
        ...toCommentHeaderMeta(thread),
        isDraft: false,
        orphaned: true,
      }))

    return [...anchoredCards, ...orphanCards]
  }

  private formatFormattingLabels(added: string[], removed: string[], colorChange: ColorChange | null): string {
    return buildFormattingChangeLabels(added, removed, colorChange, (key) => this.translateService.instant(key)).join(', ')
  }

  private formatListChangeLabels(changes: SuggestionSummary['listChanges']): string {
    return collectListChangeLabelDescriptors(changes)
      .map((descriptor) => this.translateService.instant(`EDITOR.TRACK_CHANGES.${descriptor.key}`, descriptor.params))
      .join(', ')
  }

  private highlightCard(cardId: string) {
    const sameId = this.highlightedCardId === cardId
    if (!sameId) {
      this.highlightedCardId = cardId
      this.changeDetectorRef.markForCheck()
    }
    this.scrollHighlightedCardIntoView()

    if (this.highlightTimeoutId !== null) {
      clearTimeout(this.highlightTimeoutId)
    }
    this.highlightTimeoutId = window.setTimeout(() => {
      this.highlightedCardId = null
      this.highlightTimeoutId = null
      this.changeDetectorRef.markForCheck()
    }, HIGHLIGHT_DURATION_MS)
  }

  private scrollHighlightedCardIntoView() {
    if (this.highlightedCardId === null) {
      return
    }
    const targetId = this.highlightedCardId
    queueMicrotask(() => {
      const card = this.cardRefs.find((ref) => ref.nativeElement.dataset['cardId'] === targetId)
      if (card !== undefined) {
        card.nativeElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    })
  }
}
